import { CourierListItemDto } from '@groundfloor-platform/mgmt-service-g-sdk';
import { Box } from '@mui/material';
import { PermissionBoundary } from '../../../../core/permissions/permission-boundary.component';
import { TextButton } from '../../../../ui/components/pure/text-button/text-button.component';
import { initTable } from '../../../../ui/components/pure/list-table/list-table.init';
import { pinned, useTextColumnWidth } from '../../../../ui/components/pure/list-table/list-table-column-styles';
import { TableComponents } from '../../../../ui/components/pure/list-table/list-table-cells.component';
import { Emergency } from '../../../../ui/components/pure/icons/Emergency.icon';
import { SVGIconComponent } from '../../../../ui/components/pure/icons/types';
import { Colors } from '../../../../ui/theme/colors';
import { REJECTED_BADGE_STATUS } from '../../../../ui/components/pure/user-status-badge/badge-user-status';
import { isCourierRejected } from '../../logic/courier-form.map';
import { withoutRowNavigation } from '../../logic/without-row-navigation';

const FIXED_COLUMN_WIDTHS = {
  externalID: '150px',
  mobile: '180px',
  accessCode: '120px',
  created: '150px',
  status: '180px',
  action: '160px'
} as const;

// An access code runs to 8 digits at most (`accessCodeValidator` holds the same ceiling), so the
// mask is clamped rather than trusted — a longer value from the API would otherwise overflow the
// pinned column.
const MAX_ACCESS_CODE_LENGTH = 8;
const ACCESS_CODE_ICON_SIZE = '10px';

/** One asterisk per digit, or nothing at all where the courier has no code set. */
const maskedAccessCode = (accessCodeLength: number | undefined): SVGIconComponent[] | undefined =>
  accessCodeLength
    ? Array.from({ length: Math.min(accessCodeLength, MAX_ACCESS_CODE_LENGTH) }, () => Emergency)
    : undefined;

/**
 * A rejected courier is left inactive, where suspended couriers and most AC imports also sit. The
 * status cannot tell them apart, so the badge is shown the decision instead.
 */
const badgedStatus = (row: CourierListItemDto) => (isCourierRejected(row) ? REJECTED_BADGE_STATUS : row.status);

/** The desktop column headers, which are what `hiddenColumns` names. */
export type CourierColumn =
  | 'FIRST NAME'
  | 'LAST NAME'
  | 'GROUP'
  | 'EXTERNAL ID'
  | 'MOBILE'
  | 'ACCESS CODE'
  | 'CREATED'
  | 'STATUS';

/** A button at the end of each row that hands the row to whoever supplied it. */
export interface CourierRowAction {
  label: string;
  onAction(row: CourierListItemDto): void;
  /** UI features the viewer must hold to see the button. Absent shows it to everyone. */
  availableUiFeatures?: string[];
}

function CourierRowActionCell({
  label,
  onAction,
  availableUiFeatures
}: {
  label: string;
  onAction(): void;
  availableUiFeatures?: string[];
}) {
  const button = (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end'
      }}
    >
      <TextButton color="primary" variant="outlined" onClick={withoutRowNavigation(onAction)}>
        {label}
      </TextButton>
    </Box>
  );

  return availableUiFeatures ? (
    <PermissionBoundary availableUiFeatures={availableUiFeatures}>{button}</PermissionBoundary>
  ) : (
    button
  );
}

/**
 * The `sortBy` names the endpoint accepts — its own, not the DTO's paths, so `groupName` rather than
 * `carrier.name`. Typing `sortId` against this makes anything else fail to compile; the API 400s it.
 */
interface CourierSortFields {
  createdTime: string;
  registrationTime: string;
  reviewedTime: string;
  firstName: string;
  lastName: string;
  projectName: string;
  groupName: string;
  status: string;
}

export interface CouriersTableOptions {
  /** Columns to leave out, such as the status on a list already pinned to one. */
  hiddenColumns?: CourierColumn[];
  rowAction?: CourierRowAction;
}

export function useCouriersTable({ hiddenColumns = [], rowAction }: CouriersTableOptions = {}) {
  const tb = initTable<CourierListItemDto, CourierSortFields>();
  const textWidth = useTextColumnWidth();
  const externalIDWidth = textWidth(FIXED_COLUMN_WIDTHS.externalID);
  const actionCellProps = rowAction && {
    label: () => rowAction.label,
    onAction: (row: CourierListItemDto) => () => rowAction.onAction(row),
    availableUiFeatures: () => rowAction.availableUiFeatures
  };
  tb.styles({
    table: { tableLayout: 'fixed' }
  });

  tb.rowStyles({
    td: { height: '64px' }
  });

  tb.column('FIRST NAME', TableComponents.BoldText, {
    value: ':firstName'
  }).options({
    isSortable: true,
    sortId: 'firstName',
    minBreakpoint: 'md',
    tooltip: `courier first name`
  });

  tb.column('LAST NAME', TableComponents.BoldText, {
    value: ':lastName'
  }).options({
    isSortable: true,
    sortId: 'lastName',
    minBreakpoint: 'md',
    tooltip: `courier last name`
  });

  // The group a courier belongs to, which may be a region beneath the carrier proper.
  tb.column('GROUP', TableComponents.TenantLogo, {
    name: ':carrier.name',
    imageURL: ':carrier.image'
  }).options({
    isSortable: true,
    sortId: 'groupName',
    minBreakpoint: 'md',
    tooltip: `courier's group name`
  });

  // The Text cell's own maxWidth prop is what truncates long values inside the pinned width
  tb.column('EXTERNAL ID', TableComponents.Text, {
    value: (row) => row.externalID ?? '',
    maxWidth: `~${externalIDWidth}`
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: `external ID`,
    headerStyles: pinned(externalIDWidth)
  });

  // Couriers authenticate into the mobile app by mobile number, so it identifies the row
  tb.column('MOBILE', TableComponents.Text, {
    value: ':mobile'
  }).options({
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: `courier mobile number`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.mobile)
  });

  // One asterisk per digit, nothing where no code is set. The list withholds the code itself unless
  // `includeAccessCode` is passed, so `accessCodeLength` is all a row knows — enough to mask it at
  // the right length without ever carrying the digits.
  tb.column('ACCESS CODE', TableComponents.MultipleSVGCell, {
    SVGs: (row) => maskedAccessCode(row.accessCodeLength),
    // Muted to the column-heading grey; the icon fills with `currentColor`.
    colorInput: `~${Colors.timelineGray}`,
    // Small: eight of these have to sit inside the pinned column width.
    iconSize: () => ACCESS_CODE_ICON_SIZE,
    // Never reached — the mask is already clamped — so the cell's own "N+" overflow never shows.
    maxVisibleSVGs: () => MAX_ACCESS_CODE_LENGTH,
    align: `~center`
  }).options({
    // Not one of the sortBy names the endpoint accepts.
    isSortable: false,
    minBreakpoint: 'md',
    tooltip: `courier access code`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.accessCode)
  });

  tb.column('CREATED', TableComponents.DateOnly, {
    value: ':createdTime'
  }).options({
    isSortable: true,
    sortId: 'createdTime',
    minBreakpoint: 'md',
    tooltip: `courier created date`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.created)
  });

  tb.column('STATUS', TableComponents.UserStatus, {
    status: badgedStatus
  }).options({
    isSortable: true,
    sortId: 'status',
    minBreakpoint: 'md',
    tooltip: `courier status`,
    headerStyles: pinned(FIXED_COLUMN_WIDTHS.status)
  });

  if (actionCellProps) {
    tb.column('', CourierRowActionCell, actionCellProps).options({
      isSortable: false,
      minBreakpoint: 'md',
      headerStyles: pinned(FIXED_COLUMN_WIDTHS.action)
    });
  }

  // --- Mobile Columns ---
  tb.columnGroup('FULL NAME')
    .row(TableComponents.BoldText, {
      value: ':displayName'
    })
    .row(TableComponents.Text, { value: ':mobile' })
    .options({
      isSortable: true,
      sortId: 'firstName',
      maxBreakpoint: 'sm',
      tooltip: 'courier full name'
    });

  // The badge sizes itself (110px on mobile), so the cell only has to align it, as on the
  // deliveries list — a width here would fight the badge's own responsive width
  // The mobile group's rows can't be hidden by header, so its badge follows the desktop status column.
  const mobileTrailing = tb.columnGroup('');
  if (!hiddenColumns.includes('STATUS')) {
    mobileTrailing.row(TableComponents.UserStatus, { status: badgedStatus });
  }
  mobileTrailing.row(TableComponents.DateOnly, { value: ':createdTime' });
  if (actionCellProps) {
    mobileTrailing.row(CourierRowActionCell, actionCellProps);
  }
  mobileTrailing.options({
    isSortable: false,
    maxBreakpoint: 'sm',
    styles: { display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }
  });

  hiddenColumns.forEach((column) => tb.addPredicate(column, () => false));

  return tb;
}
